/**
 * Pure read models for the card report dialog: everything that turns the
 * card-details payload (plus Scryfall printings) into display-ready values.
 * Kept out of the components so it can be unit tested without a DOM.
 */
import { getPrettyLabel } from '../../../data/CategoryPrettyDisplayUtils';
import { prettySaltCategory } from '../../../utils/SaltCategoryLabels';
import { artCropUrl } from '../report/reportUtils';

/** How the backend's effect types read in the dialog, and the text tone each gets. */
export const EFFECT_TYPE_META = {
    trigger: { label: 'Triggered ability', tone: 'red' },
    ability: { label: 'Activated ability', tone: 'amber' },
    static: { label: 'Static ability', tone: 'blue' },
    replacement: { label: 'Replacement effect', tone: 'green' },
    castingCost: { label: 'Casting cost', tone: 'gray' },
};

export function effectTypeLabel(effectType) {
    return EFFECT_TYPE_META[effectType]?.label || effectType || 'Unknown';
}

/**
 * The face the dialog should show: the one the reader clicked when it exists,
 * otherwise the front face, otherwise whatever the card has.
 */
export function resolveFaceId(cardData, preferredFaceId) {
    if (!cardData) return null;
    const faces = cardData.faces || {};
    if (preferredFaceId && faces[preferredFaceId]) return preferredFaceId;
    if (cardData.frontFaceId && faces[cardData.frontFaceId]) return cardData.frontFaceId;
    return Object.keys(faces)[0] || null;
}

function prettyActionList(actions) {
    if (!Array.isArray(actions) || actions.length === 0) return null;
    return actions.map((a) => (a || '').toString().replace(/_/g, ' ')).join(', ');
}

/** Label/value pairs describing one parsed condition or result. */
export function buildEffectSummary(condition) {
    if (!condition) return [];
    const tags = [];
    const playerThis = condition.player?.this;
    if (playerThis?.type && playerThis.type !== 'all') {
        tags.push({
            label: 'Affects',
            value: playerThis.type === 'self' ? 'you' : playerThis.type === 'opponent' ? 'opponents' : playerThis.type,
        });
    }
    const playerActions = prettyActionList(playerThis?.actions);
    if (playerActions) tags.push({ label: 'Player does', value: playerActions });
    const cardActions = prettyActionList(condition.card?.this?.actions);
    if (cardActions) tags.push({ label: 'Card does', value: cardActions });
    const fromMeaningful = (condition.zones?.from || []).filter((z) => z && z !== 'anywhere');
    const toMeaningful = (condition.zones?.to || []).filter((z) => z && z !== 'anywhere');
    if (fromMeaningful.length) tags.push({ label: 'From', value: fromMeaningful.join(', ') });
    if (toMeaningful.length) tags.push({ label: 'To', value: toMeaningful.join(', ') });
    const qualKeys = Object.keys(condition.qualifiers || {});
    if (qualKeys.length) {
        const summary = qualKeys
            .map((k) => {
                const q = condition.qualifiers[k];
                if (Array.isArray(q?.list)) return `${k}: ${q.list.join(', ')}`;
                return k;
            })
            .join(' · ');
        tags.push({ label: 'Qualifiers', value: summary });
    }
    if (condition.isEnterOrLeaveEffect) tags.push({ label: 'Flag', value: 'ETB/LTB' });
    if (condition.isOptionalPlayerChoice) tags.push({ label: 'Flag', value: 'Optional (you choose)' });
    if (condition.isSingleActivationOnly) tags.push({ label: 'Flag', value: 'Once per source' });
    if (condition.isSingleTriggerOnly) tags.push({ label: 'Flag', value: 'Once per turn' });
    return tags;
}

/**
 * Salt for the verdict tile and the saltiness section. `total` sums every
 * contributing source; the face-level `salt` (EDHREC only) is the fallback
 * for cards whose category map hasn't been built.
 */
export function saltSummary(front, cardData) {
    const categories = front?.saltCategories || cardData?.saltCategories || null;
    const raw = categories?.total ?? cardData?.salt ?? front?.salt ?? null;
    const total = raw == null || Number.isNaN(Number(raw)) ? null : Number(raw);
    const entries = categories
        ? Object.entries(categories)
              .filter(([key, val]) => key !== 'total' && Number(val?.score) > 0)
              .map(([key, val]) => ({
                  key,
                  label: prettySaltCategory(key),
                  score: Number(val.score),
              }))
              .sort((a, b) => b.score - a.score)
        : [];
    const score = total ?? 0;
    const tierLabel = score > 8 ? 'Extra salty' : score > 3 ? 'Salty' : score > 0 ? 'Mildly salty' : 'Not salty';
    return { total, tierLabel, entries };
}

/** The roles Commandersalt tagged the card with, as `{ key, label }`. */
export function roleList(categoryStats) {
    return Object.keys(categoryStats || {})
        .filter((key) => !!categoryStats[key])
        .map((key) => ({ key, label: getPrettyLabel(key) }));
}

/** `4.2` → `"$4.20"`; anything unparseable → null. */
export function formatUsd(value) {
    if (value == null || value === '') return null;
    const num = Number(value);
    if (!Number.isFinite(num)) return null;
    return `$${num.toFixed(2)}`;
}

/**
 * Scryfall ships ISO `YYYY-MM-DD`; `new Date(iso).toLocaleDateString()` would
 * shift the date across time zones, so slice the string instead.
 */
export function formatPrintDate(iso) {
    if (!iso) return '';
    const [year, month] = iso.split('-');
    if (!year || !month) return iso;
    return `${year}/${month}`;
}

/**
 * Cheapest non-foil USD printing — the market floor for grabbing a copy —
 * together with the printing itself so the buy link can point straight at it.
 */
export function cheapestPrint(prints) {
    if (!Array.isArray(prints) || prints.length === 0) return null;
    let best = null;
    for (const print of prints) {
        const usd = Number(print?.prices?.usd);
        if (!Number.isFinite(usd) || usd <= 0) continue;
        if (best == null || usd < best.usd) best = { usd, print };
    }
    return best;
}

/**
 * Where a buy link for one printing should go: that printing's TCGplayer
 * product page, or a name search when Scryfall has no URL for it.
 */
export function buyTarget(print, fallbackName) {
    if (print?.purchaseUris?.tcgplayer) return { kind: 'card', card: { purchaseUris: print.purchaseUris } };
    const query = print?.name || fallbackName;
    return query ? { kind: 'search', query } : null;
}

/**
 * The price shown in the verdict tile and on the primary buy button: the live
 * cheapest printing when Scryfall has answered, else the cached price.
 */
export function priceSummary({ prints, fallbackPrice, name }) {
    const cheapest = cheapestPrint(prints);
    if (cheapest) {
        return {
            display: formatUsd(cheapest.usd),
            isLive: true,
            print: cheapest.print,
            target: buyTarget(cheapest.print, name),
        };
    }
    const display = formatUsd(fallbackPrice);
    if (!display) return null;
    return {
        display,
        isLive: false,
        print: null,
        target: name ? { kind: 'search', query: name } : null,
    };
}

/** Every price a printing is sold at, biggest first: non-foil, then foil, then etched. */
export function printPrices(print) {
    return [
        { key: 'usd', label: null, value: formatUsd(print?.prices?.usd) },
        { key: 'foil', label: 'Foil', value: formatUsd(print?.prices?.usd_foil) },
        {
            key: 'etched',
            label: 'Etched',
            value: formatUsd(print?.prices?.usd_etched),
        },
    ].filter((price) => price.value);
}

/** Warnings a deckbuilder needs to see up front. */
export function cardFlags(cardData) {
    const flags = [];
    if (cardData?.isCommanderLegal === false) flags.push({ key: 'legality', label: 'Not legal in Commander' });
    if (cardData?.reserved === true) flags.push({ key: 'reserved', label: 'Reserved list' });
    return flags;
}

/** The power/toughness or loyalty that belongs next to a card's type line. */
export function statLine(metadata) {
    if (metadata?.power != null && metadata?.toughness != null) return `${metadata.power}/${metadata.toughness}`;
    if (metadata?.loyalty != null) return `Loyalty ${metadata.loyalty}`;
    return null;
}

/**
 * Everything the report renders for one face of one card, merged from the
 * card-details payload, the seed the opener passed (so a dialog that is still
 * loading shows the name and art it already knows), and Scryfall's printings.
 */
export function buildCardView({ cardData, seed, faceId, prints }) {
    const faces = cardData?.faces || {};
    const faceIds = Object.keys(faces);
    const activeFaceId = resolveFaceId(cardData, faceId);
    const front = activeFaceId ? faces[activeFaceId] : null;
    const metadata = front?.metadata || {};
    const context = front?.context || {};
    // Prices and searches key off the container name (the combined name for a
    // double-faced card), which is what vendors list the card under.
    const lookupName = cardData?.name || seed?.name || null;
    const imageUri =
        metadata.image_uris?.border_crop ||
        metadata.image_uris?.normal ||
        metadata.image_uris?.large ||
        seed?.imageUri ||
        null;
    const price = priceSummary({
        prints,
        fallbackPrice: cardData?.price?.usd,
        name: lookupName,
    });
    const nextFaceId = faceIds.length > 1 ? faceIds[(faceIds.indexOf(activeFaceId) + 1) % faceIds.length] : null;
    return {
        name: front?.name || lookupName || 'Card',
        typeLine: metadata.type_line || seed?.typeLine || null,
        statLine: statLine(metadata),
        manaCost: metadata.mana_cost || seed?.manaCost || '',
        oracle: metadata.oracle || seed?.oracle || '',
        cmc: metadata.cmc ?? null,
        keywords: Array.isArray(metadata.keywords) ? metadata.keywords.filter(Boolean) : [],
        imageUri,
        artCrop: metadata.image_uris?.art_crop || artCropUrl(imageUri),
        artist: metadata.artist || null,
        set: cardData?.set
            ? {
                  code: cardData.set,
                  name: cardData.set_name || cardData.set.toUpperCase(),
              }
            : null,
        roles: roleList(front?.categories?.stats),
        salt: saltSummary(front, cardData),
        flags: cardFlags(cardData),
        price,
        buyTarget: price?.target || (lookupName ? { kind: 'search', query: lookupName } : null),
        effectLines: Array.isArray(context.lines) ? context.lines : [],
        scoring: front?.scoring || {},
        context,
        canFlip: faceIds.length > 1,
        nextFaceId,
        nextFaceName: nextFaceId ? faces[nextFaceId]?.name || null : null,
    };
}
